/** Normalize pasted HTML lists and strip stuck formatting in TinyMCE. */

export function normalizePastedLists(root) {
	if (!root || root.nodeType !== 1) return;

	const doc = root.ownerDocument || document;

	const normalizeList = (listEl) => {
		if (!listEl) return;
		listEl.removeAttribute('style');
		Array.from(listEl.querySelectorAll('li')).forEach((li) => {
			li.removeAttribute('style');
			Array.from(li.querySelectorAll('span[style*="font-size"]')).forEach((span) => {
				const parent = span.parentNode;
				while (span.firstChild) parent.insertBefore(span.firstChild, span);
				parent.removeChild(span);
			});
		});
	};

	root.querySelectorAll('ul, ol').forEach(normalizeList);

	// Flatten invalid nested pseudo-lists (paragraph + manual bullet char)
	root.querySelectorAll('p').forEach((p) => {
		const text = (p.textContent || '').trim();
		if (/^[\u2022\u25CF\u25E6\-*]\s+/.test(text)) {
			const li = doc.createElement('li');
			li.innerHTML = p.innerHTML.replace(/^[\u2022\u25CF\u25E6\-*]\s*/, '');
			let ul = p.previousElementSibling;
			if (!ul || ul.tagName !== 'UL') {
				ul = doc.createElement('ul');
				p.parentNode.insertBefore(ul, p);
			}
			ul.appendChild(li);
			p.remove();
		}
	});
}

export function stripPasteArtifacts(root) {
	if (!root || root.nodeType !== 1) return;

	root.querySelectorAll('a[name], img[src^="file:"], span.mso-bookmark').forEach((el) => {
		el.remove();
	});

	root.querySelectorAll('[style*="mso-"]').forEach((el) => {
		el.removeAttribute('style');
	});
}

export function clearEmptyBoldContainers(root) {
	if (!root || root.nodeType !== 1) return;

	root.querySelectorAll('strong, b, span').forEach((el) => {
		const text = (el.textContent || '').replace(/\u00a0/g, '').trim();
		if (!text && el.childNodes.length === 0) {
			el.remove();
		}
	});

	root.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6').forEach((block) => {
		const text = (block.textContent || '').replace(/\u00a0/g, '').trim();
		const style = block.getAttribute('style') || '';
		if (!text && /font-weight\s*:\s*(bold|[6-9]00)/i.test(style)) {
			block.removeAttribute('style');
		}
	});
}

export function unwrapBlockBoldWhenEmptySelection(editor) {
	if (!editor) return;
	const node = editor.selection.getNode();
	const block = editor.dom.getParent(node, 'p,h1,h2,h3,h4,h5,h6,div,li');
	if (!block) return;

	const text = (block.textContent || '').replace(/\u00a0/g, '').trim();
	if (text) return;

	editor.dom.setStyle(block, 'font-weight', '');
	block.querySelectorAll('strong, b').forEach((el) => el.remove());
}

export async function blobToPngBlob(blob) {
	if (!blob) throw new Error('Missing image blob');
	if (typeof createImageBitmap === 'function') {
		const bitmap = await createImageBitmap(blob);
		const canvas = document.createElement('canvas');
		canvas.width = bitmap.width;
		canvas.height = bitmap.height;
		canvas.getContext('2d').drawImage(bitmap, 0, 0);
		bitmap.close?.();
		return new Promise((resolve, reject) => {
			canvas.toBlob(
				(b) => (b ? resolve(b) : reject(new Error('PNG conversion failed'))),
				'image/png',
				0.92
			);
		});
	}

	return new Promise((resolve, reject) => {
		const url = URL.createObjectURL(blob);
		const img = new Image();
		img.onload = () => {
			const canvas = document.createElement('canvas');
			canvas.width = img.naturalWidth || img.width;
			canvas.height = img.naturalHeight || img.height;
			canvas.getContext('2d').drawImage(img, 0, 0);
			URL.revokeObjectURL(url);
			canvas.toBlob(
				(b) => (b ? resolve(b) : reject(new Error('PNG conversion failed'))),
				'image/png',
				0.92
			);
		};
		img.onerror = () => {
			URL.revokeObjectURL(url);
			reject(new Error('PNG conversion failed'));
		};
		img.src = url;
	});
}

export async function svgDataUrlToPngDataUrl(svgDataUrl) {
	const res = await fetch(svgDataUrl);
	const svgBlob = await res.blob();
	const pngBlob = await blobToPngBlob(svgBlob);
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result);
		reader.onerror = reject;
		reader.readAsDataURL(pngBlob);
	});
}
